<template>
  <NuxtLayout name="catalog">
    <div class="page-search">

      <template v-if="isSearching">
        <div class="page-container">

          <div class="mt-[200px] h-[200px] text-4xl text-center">
            <AtomsGlobalLoader class="text-primary-500 mx-auto" />
          </div>
        </div>
      </template>
      <template v-else-if="searchResultsCount">

        <div class="page-container">
          <div class="mb-8">

            <MoleculesGlobalBreadcrumb :segments="[{ name: $t('Výsledky vyhledávání'), level: 0 }]"></MoleculesGlobalBreadcrumb>
            <div class="flex flex-wrap gap-6">

              <!-- groups -->

              <div class="w-full p-6 self-start tracking-normal bg-neutral-100 bg-neutral-100 desktop:w-[var(--sidebar-width)] desktop:-ml-4">
                <div class="font-bold text-3xl desktop:-ml-4 desktop:p-4">
                  {{ $t('Výsledky vyhledávání') }}
                  <div class="text-secondary-500">"{{ activeFilter.searchText }}"</div>
                </div>
                <ul class="flex flex-wrap gap-x-8 mt-6 desktop:mb-2 desktop:block">
                  <li v-for="item in searchResults.groups.items" class="page-search__subgroup">
                    <AtomsTextLink :to="item.page.url" class="page-search__subgroup__link">
                      <AtomsImagesImage :src="`${useShopZone().value.staticResourcesRoot}/img/groups/tiles/${item.id}.png`" :alt="item.name" />
                      <div>{{ item.name }}</div>
                    </AtomsTextLink>
                  </li>
                </ul>
              </div>

              <div class="flex-1">

                <div v-if="didYouMeanPhrase" class="search-box__suggest__did-you-mean" v-html="i18n.t('Zobrazeny výsledky pro <strong>{0}</strong>, jelikož pro zadaný dotaz jsme žádné výsledky nenašli. Zkontrolujte, jestli nemáte v zadání překlep, nebo zkuste napsat zadání více obecně. Pokud máte vše v pořádku, pak bohužel nemůžeme sloužit. Zkuste se podívat po něčem jiném, nebo nám napište na <a href=mailto:podpora(at)rigad.cz>podpora(at)rigad.cz</a> ať se domluvíme, co se dá případně dělat.', [didYouMeanPhrase])"></div>

                <!-- vendors -->
                <div v-if="searchResults.vendors.count" class="mb-6 p-8 bg-neutral-100">
                  <OrganismsListsVendorList :vendors="searchResults.vendors.items.map(vendor => { return { group: { id: vendor.id }, url: vendor.page.url, name: vendor.name, tags: vendor.vendor?.tags } })" />
                </div>

                <!-- warelist -->

                <div v-if="searchResults.wares.count" class="-mt-1" ref="wareListAnchor">
                  <OrganismsListsWareList :items="searchResults.wares.items" :page-size="appConfig.wareList.searchItems" :page="activeFilter.page" :total-items="searchResults.wares.count" :list-name="`Vyhledávání - ${activeFilter.searchText}`" :loading="wareListLoading" class="mb-12" />
                </div>

                <!-- magazine articles -->

                <div v-if="searchResults.magazines.count" class="mb-6 p-8 bg-neutral-100">
                  <AtomsTextHeading text-class="!bg-neutral-100">{{ $t('Magazín články') }}</AtomsTextHeading>
                  <div class="grid grid-cols-1 gap-4 mt-6 tracking-normal tablet:grid-cols-2 desktop:grid-cols-3">
                    <MoleculesBlocksMagazineItem v-for="article in searchResults.magazines.items" :key="article.page.url" :date-published="article.page.publishedAt" :date-created="article.created" :title="article.page.title" :url="article.page.url" :annotation="article.annotation" :time-to-read="article.timeToRead" :item-id="article.id" class="w-full !flex-none tablet:!flex-1" />
                  </div>
                </div>

                <!-- lexicon -->

                <div v-if="searchResults.lexicons.count" class="mb-6 p-8 bg-neutral-100">
                  <div class="mb-4 text-xl font-bold tracking-normal text-center">{{ $t('Slovník pojmů') }}</div>
                  <template v-for="lexiconItem in searchResults.lexicons.items">
                    <AtomsTextLink v-if="lexiconItem.page" :to="lexiconItem.page.url" class="block mt-2 font-bold underline tracking-normal text-center text-primary-500" :key="lexiconItem.page.url">{{ lexiconItem.page.title }}</AtomsTextLink>
                  </template>
                </div>

                <!-- recently viewed items -->

                <TemplatesRecentlyViewedItems class="mb-4" />

                <!-- usps -->

                <OrganismsBlocksUsps item-class="!bg-neutral-100 desktop:aspect-[7/3]" />

              </div>
            </div>
          </div>
        </div>

      </template>
      <template v-else>
        <div class="py-8 bg-neutral-100 tablet:py-12 desktop:py-16">
          <div class="page-container">

            <!-- search box -->

            <div class="flex flex-wrap max-w-[1200px] mx-auto">
              <AtomsFormsTextInput id="txtSearchPage" type="search" v-model="searchText" class="flex-[2] !pl-4 !border-0 !bg-neutral-50 text-neutral-500 tablet:!pl-6" :placeholder="$t('Hledáte něco?')" @enter="filterApplied('q', searchText, false, true)" />

              <MoleculesButtonsButton :show-loader="isSearching" class="flex-1" @click="filterApplied('q', searchText, false, true)">
                {{ $t('Vyhledat') }}
              </MoleculesButtonsButton>
            </div>

          </div>
        </div>

        <!-- no results description -->

        <div class="page-container">
          <div class="my-4 p-4 tracking-normal border border-neutral-200 tablet:my-8 tablet:p-8 desktop:my-16 desktop:p-16">
            <div class="mb-6 text-2xl font-bold uppercase text-primary-500">{{ $t('Pro zadaný dotaz jsme nenašli žádné výsledky') }}</div>
            <div v-html="$t(`Zkontrolujte, jestli prohledáváte <strong>všechny typy stránky</strong>, jestli nemáte v zadání <strong>překlep</strong>, nebo zkuste napsat zadání <strong>více obecně</strong>. Pokud máte vše v pořádku, pak bohužel nemůžeme sloužit. Zkuste se podívat po něčem jiném, nebo <strong>nám napište na <a href='mailto:podpora(at)rigad.cz' class='underline text-primary-500'>podpora(at)rigad.cz</a></strong> ať se domluvíme, co se dá případně dělat.`)"></div>
          </div>
        </div>
      </template>
    </div>
  </NuxtLayout>
</template>
<script setup>

import { useDebounceFn } from '@vueuse/core'

const appConfig = useAppConfig();
const route = useRoute();
const shopRouting = useShopRouting();
const locale = useLocale().getLocale();
const i18n = useI18n();

const didYouMeanPhrase = ref(null);

const hash = ref(shopRouting.parseHash());

const activeFilter = computed(() => {
  return {
    searchText: hash.value.find(q => q.key === 'q')?.value?.[0] ?? '',
    page: parseInt(hash.value.find(q => q.key === 'p')?.value?.[0]?.split('-')[0] ?? 1),
    append: hash.value.find(q => q.key === 'p')?.value?.[0]?.endsWith('-a'),
  }
});

const isSearching = ref(true);
const wareListLoading = ref(false);
const searchText = ref(activeFilter.value.searchText);

const searchResults = ref({});
const searchResultsCount = computed(() => {
  return (searchResults.value.groups?.count ?? 0) + (searchResults.value.lexicons?.count ?? 0) + (searchResults.value.magazines?.count ?? 0) + (searchResults.value.vendors?.count ?? 0) + (searchResults.value.wares?.count ?? 0);
});


// updates hash with selected filter values
const filterApplied = (urlParameter, urlValue, add, replaceValue) => {

  // reset page to 1
  const modifiedHash = shopRouting.stringifyHash(shopRouting.updateValueInHash('p', '', false, true));

  // update filtered value in hash
  const hashObj = shopRouting.updateValueInHash(urlParameter, urlValue, add, replaceValue, modifiedHash);

  // navigate to the modified hash
  shopRouting.pushHash(hashObj);

}

const search = useDebounceFn(async () => {
  if (activeFilter.value.searchText.trim().length > 2) {

    const updatedData = (await useApiFetch(`/api/search?phrase=${encodeURIComponent(didYouMeanPhrase.value ?? activeFilter.value.searchText)}&p=${activeFilter.value.page}&currencyId=${locale.currencyId}&languageId=${locale.languageId}`)).search;

    if (updatedData.suggest && updatedData.wares.count + updatedData.groups.count + updatedData.magazines.count === 0) {
      didYouMeanPhrase.value = updatedData.suggest;
      search();
      return;
    }

    if (activeFilter.value.append && searchResults.value.wares?.count) {
      searchResults.value.wares.items = searchResults.value.wares.items.concat(updatedData.wares.items);
    }
    else {
      searchResults.value = updatedData;

      useBaseAnalytics().pushEvent('search', {
        search_term: activeFilter.value.searchText,
        search_type: 'fulltext',
        search_results: searchResults.value.wares.count + searchResults.value.groups.count + searchResults.value.magazines.count,
        search_results_products: searchResults.value.wares.count,
        search_results_groups: searchResults.value.groups.count,
        search_results_articles: searchResults.value.magazines.count
      });
    }
  }

  isSearching.value = false;
  wareListLoading.value = false;

}, 100);

onMounted(() => {
  watch(() => route.hash, () => {
    if (searchResultsCount === 0) {
      isSearching.value = true;
    }

    wareListLoading.value = true;
    hash.value = shopRouting.parseHash();
    searchText.value = activeFilter.value.searchText;
    didYouMeanPhrase.value = null;
    search();
  }, { flush: 'pre', immediate: route.hash });
});

</script>
<style lang="postcss">
.page-search {
  --sidebar-width: 360px;

  &__groups {
    @apply mb-3 tracking-normal bg-neutral-100 bg-neutral-100 desktop:w-[var(--sidebar-width)] desktop:-ml-4 desktop:mb-8;
  }

  &__subgroup {
    @apply w-full transition-colors tablet:w-[calc(50%-1rem)] desktop:w-auto desktop:-mr-6;

    &__link {
      @apply flex gap-4 items-center border-t border-neutral-200 hover:no-underline desktop:mr-6;

      img {
        @apply w-[40px] max-h-[40px] object-contain m-4;
      }

      div {
        @apply flex-1 py-6 pr-6 text-lg desktop:-mr-6;
      }
    }

    &:hover,
    &--current {
      @apply bg-neutral-400 text-neutral-50;

      .page-search__subgroup__link {
        @apply !mt-[1px] !border-t-0;
      }
    }

    &:first-of-type &__link {
      @apply border-t-0 mt-[1px];
    }

    @screen tablet {
      &:nth-of-type(2) &__link {
        @apply border-t-0 mt-[1px];
      }
    }

    @screen desktop {
      &:nth-of-type(2) &__link {
        @apply border-t mt-0;
      }
    }
  }
}
</style>
